import { useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { useReactive } from 'ahooks';
import { Button, Col, Collapse, Row, Table, message } from 'antd';
import { map } from 'lodash';
import moment from 'moment';

import { ScheduleExceptionSuggestPeriodsResp } from '@api/responseModels/worker/schedule/scheduleExceprionSuggestPeriods';
import { ScheduleExceptionListResp } from '@api/responseModels/worker/schedule/scheduleExceptionListResponse';
import { ScheduleExceptionResp } from '@api/responseModels/worker/schedule/scheduleExceptionResponse';
import { ScheduleExceptionStatusListResp } from '@api/responseModels/worker/schedule/scheduleExceptionStatusListResponse';
import { ScheduleExceptionTypeListResp } from '@api/responseModels/worker/schedule/scheduleExceptionTypeListResponse';
import { Loader } from '@atoms/loader';
import { Common } from '@classes/commonMethods';
import { CRMAPIManager } from '@classes/crmApiManager';
import {
    TScheduleException,
    TScheduleExceptionStatus,
    TScheduleExceptionType,
} from '@components/schedule/common/types';
import { useCheckEventOverlap } from '@components/schedule/hooks/useCheckEventOverlap';
import { faCaretDown, faCaretRight, faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DurationPicker } from '@organisms/pickers/durationPicker';
import { useQuery } from '@tanstack/react-query';

import { ScheduleExceptionModal } from '../exceptionModal/exceptionModal';

const { Panel } = Collapse;

/**
 * Статус «Ожидает» в справочнике. Индекс, а не поиск по имени, — так было
 * изначально; вынесено в константу, чтобы магическая двойка не повторялась.
 */
const DEFAULT_EXCEPTION_STATUS_INDEX = 2;

type TProps = {
    editing: boolean;
};

type TState = {
    isLoading: boolean;
    exceptionStatuses: TScheduleExceptionStatus[];
    exceptionTypes: TScheduleExceptionType[];
    exceptions: TScheduleException[];
    curException: TScheduleException;
    modalOpen: boolean;
    durationPickerVisible: boolean;
    availablePeriods: TimePeriod[];
};

export type TimePeriod = {
    from?: moment.Moment;
    /**
     * Разница в минутах
     */
    duration?: number;
    to?: moment.Moment | string;
};

const ScheduleExceptionsPanel = ({ editing }: TProps): JSX.Element => {
    const state = useReactive<TState>({
        isLoading: false,
        exceptionStatuses: [],
        exceptionTypes: [],
        exceptions: [],
        curException: null,
        modalOpen: false,
        durationPickerVisible: false,
        availablePeriods: [],
    });
    const { crmID, workerPositionID } = useParams();
    const [messageApi, contextHolder] = message.useMessage();
    const loadAllExc = workerPositionID == undefined;

    async function loadExceptions() {
        state.isLoading = true;
        try {
            const set = await CRMAPIManager.request<ScheduleExceptionTypeListResp>(async (api) => {
                return await api.getScheduleExceptionTypeList();
            });
            if (set.errorCode) throw set;
            state.exceptionTypes = set.data.data;
            const ses = await CRMAPIManager.request<ScheduleExceptionStatusListResp>(
                async (api) => {
                    return await api.getScheduleExceptionStatusList();
                }
            );
            if (ses.errorCode) throw ses;
            state.exceptionStatuses = ses.data.data;
            const se = await CRMAPIManager.request<ScheduleExceptionListResp>(async (api) => {
                if (loadAllExc) {
                    return await api.getScheduleExceptionList(+crmID);
                } else {
                    return await api.getScheduleExceptionList(+crmID, {
                        worker_position: +workerPositionID,
                    });
                }
            });
            if (se.errorCode) throw se;
            state.exceptions = se.data.data;
        } catch (err) {
            messageApi.error('Ошибка при загрузке исключений');
            console.log(err);
        }
        state.isLoading = false;
    }

    function validateException() {
        return true;
    }

    async function saveException() {
        if (!validateException()) return;
        state.isLoading = true;
        try {
            const result = await CRMAPIManager.request<ScheduleExceptionResp>(async (api) => {
                if (state.curException.id == null) {
                    return await api.saveScheduleException({
                        ...state.curException,
                        crm_id: [+crmID],
                        worker_positions: workerPositionID ? [+workerPositionID] : [],
                        status: state.exceptionStatuses[DEFAULT_EXCEPTION_STATUS_INDEX],
                    });
                } else {
                    return await api.updateScheduleException(state.curException);
                }
            });
            if (result.errorCode) throw result;
            messageApi.success(
                `Исключение ${state.curException.id == null ? 'создано' : 'обновлено'}`
            );
            state.modalOpen = false;
            state.curException = null;
            await loadExceptions();
        } catch (err) {
            messageApi.error(
                `Ошибка при ${
                    state.curException.id == null ? 'добавлении' : 'обновлении'
                } исключения`
            );
            console.log(err);
        }
        state.isLoading = false;
    }

    async function deleteException() {
        state.isLoading = true;
        try {
            const result = await CRMAPIManager.request<ScheduleExceptionResp>(async (api) => {
                return await api.removeScheduleException(+crmID, state.curException?.id);
            });
            if (result.errorCode) throw result;
            messageApi.success('Исключение удалено');
            state.modalOpen = false;
            state.curException = null;
            await loadExceptions();
        } catch (err) {
            messageApi.error('Ошибка при удалении исключения');
            console.log(err);
        }
        state.isLoading = false;
    }

    useEffect(() => {
        loadExceptions();
    }, [crmID]);

    function onAddExceptionClick() {
        // Тип и статус — обязательная часть исключения: без них форма нечем заполнена,
        // а модалка читает `type.name`/`type.min_duration` и падает на undefined.
        // Пустой справочник значит, что типы не заведены для этой CRM либо ещё грузятся.
        const [defaultType] = state.exceptionTypes;
        const defaultStatus = state.exceptionStatuses[DEFAULT_EXCEPTION_STATUS_INDEX];

        if (!defaultType || !defaultStatus) {
            messageApi.warning(
                state.isLoading
                    ? 'Справочник исключений ещё загружается'
                    : 'Не заведены типы изменений графика — добавить исключение нельзя'
            );
            return;
        }

        const tomorrow = moment().add(1, 'days');
        state.curException = {
            crm_id: [+crmID],
            id: null,
            type: defaultType,
            duration: defaultType.min_duration,
            start_date: tomorrow.format('DD-MM-YYYY'),
            start_time: '08:00',
            end_date: tomorrow.format('DD-MM-YYYY'),
            end_time: moment('08:00', 'HH:mm')
                .add(defaultType.min_duration, 'minutes')
                .format('HH:mm'),
            status: defaultStatus,
            worker_positions: [],
            description: '',
        };
        state.modalOpen = true;
    }

    function onChangeDuration(duration: number, _): void {
        state.durationPickerVisible = false;
        const start = moment(
            state.curException.start_date + 'T' + state.curException.start_time,
            'DD-MM-YYYYTHH:mm'
        );
        const end = start.clone().add(duration, 'minutes');
        state.curException = {
            ...state.curException,
            duration: duration,
            end_date: end.format('DD-MM-YYYY'),
            end_time: end.format('HH:mm'),
        };
    }

    function onCancelDuration(): void {
        state.durationPickerVisible = false;
    }

    const { conflicts } = useCheckEventOverlap({
        curException: state.curException,
    });

    const getPeriods = async () => {
        try {
            const resp = await CRMAPIManager.request<ScheduleExceptionSuggestPeriodsResp>(
                async (api) => {
                    return await api.getScheduleExceptionSuggestPeriods({
                        worker_positions: [workerPositionID ? +workerPositionID : null],
                        duration: state.curException?.duration,
                        start_date: moment().format('DD-MM-YYYY'),
                        start_time: moment().format('HH:mm'),
                    });
                }
            );

            state.availablePeriods = map(resp.data.suggested_periods, (p) => {
                return {
                    from: moment(p.start_date + 'T' + p.start_time, 'DD-MM-YYYYTHH:mm'),
                    duration: p.duration,
                    to:
                        p.end_date !== '~' && p.end_time !== '~'
                            ? moment(p.end_date + 'T' + p.end_time, 'DD-MM-YYYYTHH:mm').add(
                                  p.duration,
                                  'minutes'
                              )
                            : 'и позже',
                };
            });

            // state.availablePeriods = periods.data.data;
        } catch (err) {
            console.log(err);
        }
    };

    const {} = useQuery({
        queryKey: [state.curException?.type?.id, state.curException?.duration],
        enabled: !!state.curException?.type?.id,
        queryFn: getPeriods,
    });

    return (
        <>
            {contextHolder}
            {state.isLoading && <Loader />}
            <ScheduleExceptionModal
                availablePeriods={state.availablePeriods as any}
                curException={state.curException}
                exceptionStatuses={state.exceptionStatuses}
                exceptionTypes={state.exceptionTypes}
                exceptionEditMode={!editing}
                exceptionModal={state.modalOpen}
                onClose={() => (state.modalOpen = false)}
                onDelete={deleteException}
                onSave={saveException}
                setCurException={(exc) => {
                    state.curException = exc;
                    return state.curException;
                }}
                setDurationPickerVisible={(val) => {
                    state.durationPickerVisible = val;
                }}
                messageApi={messageApi}
                conflicts={conflicts}
            />
            <DurationPicker
                isModalOpen={state.durationPickerVisible}
                onOk={onChangeDuration}
                onCancel={onCancelDuration}
                defaultValue={state.curException?.duration}
                showTimeCountMethod={false}
                totalTime={false}
                showMinutes={!state.curException?.type?.is_daily_boundary}
                showHours={!state.curException?.type?.is_daily_boundary}
                showDays={state.curException?.type?.is_daily_boundary}
                showMonths={false}
            />
            <Collapse
                bordered={false}
                defaultActiveKey={['exceptions']}
                expandIcon={({ isActive }) => (
                    <FontAwesomeIcon
                        className="bigger-caret"
                        icon={isActive ? faCaretDown : faCaretRight}
                    />
                )}
                expandIconPlacement="end"
                styles={{
                    header: {
                        backgroundColor: 'rgb(255, 201, 140)',
                        border: '2px solid rgb(154, 101, 59)',
                        borderRadius: 12,
                        color: 'black',
                        fontSize: 16,
                        fontWeight: 500,
                        alignItems: 'center',
                        cursor: 'default',
                    },
                    body: {
                        backgroundColor: 'rgb(255, 201, 140)',
                        border: '2px solid rgb(154, 101, 59)',
                        borderTop: 'none',
                        borderBottomLeftRadius: 12,
                        borderBottomRightRadius: 12,
                        padding: 0,
                    },
                }}
            >
                <Panel
                    header="Изменения в графике:"
                    key="exceptions"
                    extra={
                        <Button
                            className="green-btn btn-icon-separated"
                            disabled={!editing}
                            icon={<FontAwesomeIcon icon={faPlus} />}
                            onClick={(event) => {
                                event.stopPropagation();
                                onAddExceptionClick();
                            }}
                            title="Добавить изменение"
                            type="default"
                        >
                            Добавить
                        </Button>
                    }
                >
                    <Row className="table-row hcentered">
                        {state.exceptions.length > 0 ? (
                            <Table
                                bordered={true}
                                columns={[
                                    {
                                        title: 'Тип',
                                        dataIndex: 'type',
                                        width: '70px',
                                        key: 'type',
                                        render: (val) => val?.name,
                                    },
                                    {
                                        title: 'Прод.',
                                        dataIndex: 'duration',
                                        key: 'duration',
                                        width: '80px',
                                        render: (val) => Common.convertMinutes2Date(val),
                                    },
                                    {
                                        title: 'Начало //\nЗавершение',
                                        dataIndex: 'start_date',
                                        key: 'start_at',
                                        render: (_, record) => (
                                            <Col style={{ minWidth: '120px' }}>
                                                <Row>
                                                    {`${record.start_time} / ${moment(
                                                        record.start_date,
                                                        'DD-MM-YYYY'
                                                    ).format('DD.MM.YYYY')} //`}
                                                </Row>
                                                <Row>
                                                    {`${record.end_time} / ${moment(
                                                        record.end_date,
                                                        'DD-MM-YYYY'
                                                    ).format('DD.MM.YYYY')}`}
                                                </Row>
                                            </Col>
                                        ),
                                    },
                                    {
                                        title: 'Статус',
                                        dataIndex: 'status',
                                        width: '75px',
                                        key: 'status',
                                        render: (val) => val?.name,
                                    },
                                ]}
                                className="exception-table"
                                dataSource={state.exceptions}
                                rowClassName={(record) => {
                                    switch (record.status?.name) {
                                        case 'Завершено':
                                            return 'exception-finished';
                                        case 'Активно':
                                            return 'exception-active';
                                        default:
                                            return 'exception-regular';
                                    }
                                }}
                                onRow={(record, _) => {
                                    return {
                                        onClick: (_) => {
                                            if (record.status?.name == 'Завершено') return;
                                            state.curException = record;
                                            state.modalOpen = true;
                                        },
                                    };
                                }}
                                pagination={{
                                    pageSize: 5,
                                }}
                                rowKey="id"
                            />
                        ) : (
                            <p className="vpadded">Вы ещё не добавили изменений :)</p>
                        )}
                    </Row>
                </Panel>
            </Collapse>
        </>
    );
};

export { ScheduleExceptionsPanel };
